import type { MsgKey } from "./i18n";

/**
 * 说明视频的唯一清单：课程（course）→ 课（lesson）两层。页面上的「看视频」入口和「教程 → 课程中心」页都从这里读，
 * 加课只改这一处：在对应课程的 lessons 里登记一条、补三种语言的文案、旁白稿放 docs/help-videos/。
 * 加一门课程同理：往 HELP_COURSES 里加一项，课程中心自动多一个分区，导航不用动。
 *
 * 视频与封面不进 git：传到公司上传服务（CDN），这里只存完整 https 链接。
 * 还没做好的课 src / poster 留空串、duration 0：入口照常显示，点开提示「视频制作中」。
 *
 * lessons 的顺序就是课的顺序：后一课用到前一课的概念。
 *
 * 视频是中文配音；英日文的标题由 hv.modalTitle 补「视频为中文」。
 */
export type HelpTrackId = "operations" | "client" | "server" | "design" | "qa" | "hello-ludo";
export interface HelpTrackDef {
  id: HelpTrackId;
  title: MsgKey;
  summary: MsgKey;
}
export const HELP_TRACKS: HelpTrackDef[] = [
  { id: "operations", title: "ht.operations.title", summary: "ht.operations.summary" },
  { id: "client", title: "ht.client.title", summary: "ht.client.summary" },
  { id: "server", title: "ht.server.title", summary: "ht.server.summary" },
  { id: "design", title: "ht.design.title", summary: "ht.design.summary" },
  { id: "qa", title: "ht.qa.title", summary: "ht.qa.summary" },
  { id: "hello-ludo", title: "ht.helloLudo.title", summary: "ht.helloLudo.summary" },
];
export type HelpCourseId = "version" | "tenant" | "cost" | "client" | "dev-flow" | "operations" | "qa"
  | "global" | "board" | "clear" | "pettown" | "match" | "content" | "hello-ludo";
export type HelpVideoId =
  | "ver-publish" | "ver-live" | "ver-changes" | "ver-rollback"
  | "agents" | "init"
  | "b" | "e" | "rolls" | "rho" | "k" | "overview" | "source"
  | "client-overview" | "client-mcp" | "client-auth" | "client-resources"
  | "client-state" | "client-ably" | "client-debug" | "client-release"
  | "ops-players" | "ops-mail" | "ops-release"
  | "dev-pr" | "dev-merge" | "dev-release"
  | "qa-setup" | "qa-scenarios" | "qa-recovery" | "qa-report"
  | "dice" | "fight" | "daily" | "spin" | "subgame" | "correct" | "fightval" | "actitem" | "reward" | "talent"
  | "ptparams" | "ptstar" | "pttalent" | "mroom" | "mrules" | "mecon" | "mstage" | "mcells" | "cards" | "puzzle" | "pets"
  | "hl-route" | "hl-ver-publish" | "hl-init" | "hl-dice" | "hl-spin" | "hl-subgame" | "hl-talent"
  | "hl-ptparams" | "hl-ptstar" | "hl-pttalent" | "hl-roll" | "hl-enter" | "hl-resources" | "hl-map" | "hl-new" | "hl-gaps" | "hl-correct";

export interface HelpVideoDef {
  id: HelpVideoId;
  /** 短标题，如「E 是什么」；悬停提示、弹窗标题、课程卡片都用它 */
  title: MsgKey;
  /** 一句话简介，课程卡片上显示 */
  summary: MsgKey;
  /** 秒；还没做好的课填 0 */
  duration: number;
  /** 完整 https 链接（CDN）；还没做好的课留空串 */
  src: string;
  /** 完整 https 链接（CDN）；还没做好的课留空串 */
  poster: string;
  /**
   * 讲的是哪一页：cost = 总造价页（param 是 DiceParams 的键；B 值、公式总览、造价来源不是单个可填字段）；
   * version = 每个配置页顶部的版本栏；agents / init = 代理管理页、初始化配置页；
   * config = 普通配置页的整页讲解，讲到哪几页由 hashes 列出（入口挂在那几页的面包屑旁）；
   * client / operations / qa = 岗位课程，讲的是流程而不是某个配置页，用到了哪页同样可以用 hashes 挂入口
   */
  page: "cost" | "version" | "agents" | "init" | "client" | "operations" | "qa" | "config";
  param?: string;
  /**
   * 这节课讲到的管端页面 id（即 hash 去掉开头的 #，如 "global-dice"），入口挂在那几页的面包屑旁；不填就只在课程中心出现。
   * 末尾带 * 是前缀匹配："subgame-*" 挂到所有副玩法格子页（格子由服务端配置，页面 id 不固定）。
   * 一页可以挂多节课，入口按清单顺序排。见 lessonsForPage。
   */
  hashes?: string[];
}

export interface HelpCourseDef {
  id: HelpCourseId;
  track: HelpTrackId;
  title: MsgKey;
  summary: MsgKey;
  lessons: HelpVideoDef[];
}

/** 公司上传服务（Bucket Bridge）的地址：每个文件一个上传时刻目录，链接要整条照抄，拼不出来 */
const CDN = "https://resource.bingo84.com/pp";

/** 各学习路径内按清单顺序学习；未制作的课保留大纲。 */
const BASE_COURSES: HelpCourseDef[] = [
  {
    id: "version", track: "operations", title: "hv.course.version.title", summary: "hv.course.version.summary",
    lessons: [
      { id: "ver-publish", title: "hv.verPublish.title", summary: "hv.verPublish.summary", duration: 71, src: `${CDN}/2026-10-16T15-03-58-172Z/aeroplane/help-video/course-ver-publish.mp4`,
        poster: `${CDN}/2026-10-16T15-03-59-419Z/aeroplane/help-video/course-ver-publish-poster.webp`,
        page: "version" },
      { id: "ver-live", title: "hv.verLive.title", summary: "hv.verLive.summary", duration: 82, src: `${CDN}/2026-10-16T15-04-00-538Z/aeroplane/help-video/course-ver-live.mp4`,
        poster: `${CDN}/2026-10-16T15-04-01-689Z/aeroplane/help-video/course-ver-live-poster.webp`,
        page: "version" },
      { id: "ver-changes", title: "hv.verChanges.title", summary: "hv.verChanges.summary", duration: 67, src: `${CDN}/2026-10-16T15-04-02-755Z/aeroplane/help-video/course-ver-changes.mp4`,
        poster: `${CDN}/2026-10-16T15-04-03-979Z/aeroplane/help-video/course-ver-changes-poster.webp`,
        page: "version" },
      { id: "ver-rollback", title: "hv.verRollback.title", summary: "hv.verRollback.summary", duration: 64, src: `${CDN}/2026-10-16T15-04-05-273Z/aeroplane/help-video/course-ver-rollback.mp4`,
        poster: `${CDN}/2026-10-16T15-04-06-424Z/aeroplane/help-video/course-ver-rollback-poster.webp`,
        page: "version" },
    ],
  },
  {
    id: "tenant", track: "operations", title: "hv.course.tenant.title", summary: "hv.course.tenant.summary",
    lessons: [
      { id: "agents", title: "hv.agents.title", summary: "hv.agents.summary", duration: 68, src: `${CDN}/2026-10-16T15-04-07-637Z/aeroplane/help-video/course-agents.mp4`,
        poster: `${CDN}/2026-10-16T15-04-08-727Z/aeroplane/help-video/course-agents-poster.webp`,
        page: "agents" },
      { id: "init", title: "hv.init.title", summary: "hv.init.summary", duration: 88, src: `${CDN}/2026-10-16T15-04-10-112Z/aeroplane/help-video/course-init.mp4`,
        poster: `${CDN}/2026-10-16T15-04-11-230Z/aeroplane/help-video/course-init-poster.webp`,
        page: "init" },
    ],
  },
  {
    id: "cost", track: "design", title: "hv.course.cost.title", summary: "hv.course.cost.summary",
    lessons: [
      { id: "b", title: "hv.b.title", summary: "hv.b.summary", duration: 48, src: `${CDN}/2026-10-16T09-56-10-032Z/aeroplane/help-video/cost-help-b.mp4`,
        poster: `${CDN}/2026-10-16T10-04-49-026Z/aeroplane/help-video/cost-help-b-poster.webp`,
        page: "cost" },
      { id: "e", title: "hv.e.title", summary: "hv.e.summary", duration: 50, src: `${CDN}/2026-10-16T09-56-12-688Z/aeroplane/help-video/cost-help-e.mp4`,
        poster: `${CDN}/2026-10-16T09-56-36-554Z/aeroplane/help-video/cost-help-e-poster.webp`,
        page: "cost", param: "E" },
      { id: "rolls", title: "hv.rolls.title", summary: "hv.rolls.summary", duration: 79, src: `${CDN}/2026-10-16T10-40-48-101Z/aeroplane/help-video/cost-help-rolls.mp4`,
        poster: `${CDN}/2026-10-16T10-40-49-252Z/aeroplane/help-video/cost-help-rolls-poster.webp`,
        page: "cost", param: "targetBase" },
      { id: "rho", title: "hv.rho.title", summary: "hv.rho.summary", duration: 54, src: `${CDN}/2026-10-16T09-56-14-831Z/aeroplane/help-video/cost-help-rho.mp4`,
        poster: `${CDN}/2026-10-16T09-56-37-613Z/aeroplane/help-video/cost-help-rho-poster.webp`,
        page: "cost", param: "rho" },
      { id: "k", title: "hv.k.title", summary: "hv.k.summary", duration: 80, src: `${CDN}/2026-10-16T09-56-16-901Z/aeroplane/help-video/cost-help-k.mp4`,
        poster: `${CDN}/2026-10-16T09-56-38-551Z/aeroplane/help-video/cost-help-k-poster.webp`,
        page: "cost", param: "incomeK" },
      { id: "overview", title: "hv.overview.title", summary: "hv.overview.summary", duration: 72, src: `${CDN}/2026-10-16T10-40-50-320Z/aeroplane/help-video/cost-help-overview.mp4`,
        poster: `${CDN}/2026-10-16T10-40-51-376Z/aeroplane/help-video/cost-help-overview-poster.webp`,
        page: "cost" },
      { id: "source", title: "hv.source.title", summary: "hv.source.summary", duration: 78, src: `${CDN}/2026-10-16T10-40-52-465Z/aeroplane/help-video/cost-help-source.mp4`,
        poster: `${CDN}/2026-10-16T10-40-53-482Z/aeroplane/help-video/cost-help-source-poster.webp`,
        page: "cost" },
    ],
  },
  {
    id: "global", track: "design", title: "hv.course.global.title", summary: "hv.course.global.summary",
    lessons: [
      { id: "dice", title: "hv.dice.title", summary: "hv.dice.summary", duration: 82, src: `${CDN}/2026-10-16T16-09-40-879Z/aeroplane/help-video/course-dice.mp4`,
        poster: `${CDN}/2026-10-16T16-09-42-131Z/aeroplane/help-video/course-dice-poster.webp`,
        page: "config", hashes: ["global-dice"] },
      { id: "fight", title: "hv.fight.title", summary: "hv.fight.summary", duration: 88, src: `${CDN}/2026-10-16T16-11-45-780Z/aeroplane/help-video/course-fight.mp4`,
        poster: `${CDN}/2026-10-16T16-11-47-876Z/aeroplane/help-video/course-fight-poster.webp`,
        page: "config", hashes: ["global-fight"] },
      { id: "daily", title: "hv.daily.title", summary: "hv.daily.summary", duration: 75, src: `${CDN}/2026-10-23T04-04-03-572Z/aeroplane/help-video/course-daily.mp4`,
        poster: `${CDN}/2026-10-23T04-04-06-635Z/aeroplane/help-video/course-daily-poster.webp`,
        page: "config", hashes: ["global-fight"] },
    ],
  },
  {
    id: "board", track: "design", title: "hv.course.board.title", summary: "hv.course.board.summary",
    lessons: [
      { id: "spin", title: "hv.spin.title", summary: "hv.spin.summary", duration: 116, src: `${CDN}/2026-10-16T16-15-12-986Z/aeroplane/help-video/course-spin.mp4`,
        poster: `${CDN}/2026-10-16T16-15-14-417Z/aeroplane/help-video/course-spin-poster.webp`,
        page: "config", hashes: ["spin"] },
      { id: "subgame", title: "hv.subgame.title", summary: "hv.subgame.summary", duration: 98, src: `${CDN}/2026-10-16T16-16-42-427Z/aeroplane/help-video/course-subgame.mp4`,
        poster: `${CDN}/2026-10-16T16-16-43-619Z/aeroplane/help-video/course-subgame-poster.webp`,
        page: "config", hashes: ["subgame-*"] },
      { id: "fightval", title: "hv.fightval.title", summary: "hv.fightval.summary", duration: 116, src: `${CDN}/2026-10-16T16-19-20-839Z/aeroplane/help-video/course-fightval.mp4`,
        poster: `${CDN}/2026-10-16T16-19-22-085Z/aeroplane/help-video/course-fightval-poster.webp`,
        page: "config", hashes: ["subgame-thunder", "subgame-visit", "subgame-omamori"] },
      { id: "actitem", title: "hv.actitem.title", summary: "hv.actitem.summary", duration: 80, src: `${CDN}/2026-10-16T16-20-33-732Z/aeroplane/help-video/course-actitem.mp4`,
        poster: `${CDN}/2026-10-16T16-20-34-765Z/aeroplane/help-video/course-actitem-poster.webp`,
        page: "config", hashes: ["act-item"] },
      { id: "correct", title: "hv.correct.title", summary: "hv.correct.summary", duration: 158, src: `${CDN}/2026-10-21T07-40-38-344Z/aeroplane/help-video/course-correct.mp4`,
        poster: `${CDN}/2026-10-21T07-40-39-624Z/aeroplane/help-video/course-correct-poster.webp`,
        page: "config", hashes: ["spin"] },
    ],
  },
  {
    id: "clear", track: "design", title: "hv.course.clear.title", summary: "hv.course.clear.summary",
    lessons: [
      { id: "reward", title: "hv.reward.title", summary: "hv.reward.summary", duration: 74, src: `${CDN}/2026-10-16T16-23-06-871Z/aeroplane/help-video/course-reward.mp4`,
        poster: `${CDN}/2026-10-16T16-23-08-050Z/aeroplane/help-video/course-reward-poster.webp`,
        page: "config", hashes: ["reward"] },
      { id: "talent", title: "hv.talent.title", summary: "hv.talent.summary", duration: 97, src: `${CDN}/2026-10-16T16-29-31-972Z/aeroplane/help-video/course-talent.mp4`,
        poster: `${CDN}/2026-10-16T16-29-33-211Z/aeroplane/help-video/course-talent-poster.webp`,
        page: "config", hashes: ["level-talent"] },
    ],
  },
  {
    id: "pettown", track: "design", title: "hv.course.pettown.title", summary: "hv.course.pettown.summary",
    lessons: [
      { id: "ptparams", title: "hv.ptparams.title", summary: "hv.ptparams.summary", duration: 99, src: `${CDN}/2026-10-23T04-04-25-836Z/aeroplane/help-video/course-ptparams.mp4`,
        poster: `${CDN}/2026-10-23T04-04-28-130Z/aeroplane/help-video/course-ptparams-poster.webp`,
        page: "config", hashes: ["pettown-params"] },
      { id: "ptstar", title: "hv.ptstar.title", summary: "hv.ptstar.summary", duration: 127, src: `${CDN}/2026-10-20T05-39-18-742Z/aeroplane/help-video/course-ptstar.mp4`,
        poster: `${CDN}/2026-10-20T05-39-19-974Z/aeroplane/help-video/course-ptstar-poster.webp`,
        page: "config", hashes: ["pettown-star", "pettown-slot"] },
      { id: "pttalent", title: "hv.pttalent.title", summary: "hv.pttalent.summary", duration: 102, src: `${CDN}/2026-10-20T05-21-21-716Z/aeroplane/help-video/course-pttalent.mp4`,
        poster: `${CDN}/2026-10-20T05-21-23-333Z/aeroplane/help-video/course-pttalent-poster.webp`,
        page: "config", hashes: ["pettown-talent"] },
    ],
  },
  {
    id: "match", track: "design", title: "hv.course.match.title", summary: "hv.course.match.summary",
    lessons: [
      { id: "mroom", title: "hv.mroom.title", summary: "hv.mroom.summary", duration: 88, src: `${CDN}/2026-10-16T16-42-23-698Z/aeroplane/help-video/course-mroom.mp4`,
        poster: `${CDN}/2026-10-16T16-42-24-976Z/aeroplane/help-video/course-mroom-poster.webp`,
        page: "config", hashes: ["match-mode"] },
      { id: "mrules", title: "hv.mrules.title", summary: "hv.mrules.summary", duration: 85, src: `${CDN}/2026-10-16T16-46-23-449Z/aeroplane/help-video/course-mrules.mp4`,
        poster: `${CDN}/2026-10-16T16-46-24-691Z/aeroplane/help-video/course-mrules-poster.webp`,
        page: "config", hashes: ["match-mode"] },
      { id: "mecon", title: "hv.mecon.title", summary: "hv.mecon.summary", duration: 74, src: `${CDN}/2026-10-16T16-50-30-585Z/aeroplane/help-video/course-mecon.mp4`,
        poster: `${CDN}/2026-10-16T16-50-31-678Z/aeroplane/help-video/course-mecon-poster.webp`,
        page: "config", hashes: ["match-mode"] },
      { id: "mstage", title: "hv.mstage.title", summary: "hv.mstage.summary", duration: 67, src: `${CDN}/2026-10-16T16-52-18-853Z/aeroplane/help-video/course-mstage.mp4`,
        poster: `${CDN}/2026-10-16T16-52-19-873Z/aeroplane/help-video/course-mstage-poster.webp`,
        page: "config", hashes: ["battle-stage", "ability-card"] },
      { id: "mcells", title: "hv.mcells.title", summary: "hv.mcells.summary", duration: 81, src: `${CDN}/2026-10-16T16-54-57-624Z/aeroplane/help-video/course-mcells.mp4`,
        poster: `${CDN}/2026-10-16T16-54-58-856Z/aeroplane/help-video/course-mcells-poster.webp`,
        page: "config", hashes: ["board-cells", "lamp-drops"] },
    ],
  },
  {
    id: "content", track: "operations", title: "hv.course.content.title", summary: "hv.course.content.summary",
    lessons: [
      { id: "cards", title: "hv.cards.title", summary: "hv.cards.summary", duration: 75, src: `${CDN}/2026-10-16T17-07-41-041Z/aeroplane/help-video/course-cards.mp4`,
        poster: `${CDN}/2026-10-16T17-07-42-256Z/aeroplane/help-video/course-cards-poster.webp`,
        page: "config", hashes: ["card-item", "card-drop", "card-idol"] },
      { id: "puzzle", title: "hv.puzzle.title", summary: "hv.puzzle.summary", duration: 60, src: `${CDN}/2026-10-16T17-09-01-896Z/aeroplane/help-video/course-puzzle.mp4`,
        poster: `${CDN}/2026-10-16T17-09-02-999Z/aeroplane/help-video/course-puzzle-poster.webp`,
        page: "config", hashes: ["puzzle-list", "puzzle-price"] },
      { id: "pets", title: "hv.pets.title", summary: "hv.pets.summary", duration: 66, src: `${CDN}/2026-10-16T17-11-08-674Z/aeroplane/help-video/course-pets.mp4`,
        poster: `${CDN}/2026-10-16T17-11-09-859Z/aeroplane/help-video/course-pets-poster.webp`,
        page: "config", hashes: ["pet-list", "pet-star"] },
    ],
  },
  {
    id: "client", track: "client", title: "hv.course.client.title", summary: "hv.course.client.summary",
    lessons: [
      { id: "client-overview", title: "hv.clientOverview.title", summary: "hv.clientOverview.summary", duration: 78, src: `${CDN}/2026-10-16T21-00-55-742Z/aeroplane/help-video/course-client-overview.mp4`,
        poster: `${CDN}/2026-10-16T21-00-56-711Z/aeroplane/help-video/course-client-overview-poster.webp`,
        page: "client" },
      { id: "client-mcp", title: "hv.clientMcp.title", summary: "hv.clientMcp.summary", duration: 93, src: `${CDN}/2026-10-16T21-02-55-748Z/aeroplane/help-video/course-client-mcp.mp4`,
        poster: `${CDN}/2026-10-16T21-02-56-733Z/aeroplane/help-video/course-client-mcp-poster.webp`,
        page: "client" },
      { id: "client-auth", title: "hv.clientAuth.title", summary: "hv.clientAuth.summary", duration: 116, src: `${CDN}/2026-10-16T21-05-02-914Z/aeroplane/help-video/course-client-auth.mp4`,
        poster: `${CDN}/2026-10-16T21-05-03-911Z/aeroplane/help-video/course-client-auth-poster.webp`,
        page: "client", hashes: ["dev-token"] },
      { id: "client-resources", title: "hv.clientResources.title", summary: "hv.clientResources.summary", duration: 74, src: `${CDN}/2026-10-16T21-06-21-991Z/aeroplane/help-video/course-client-resources.mp4`,
        poster: `${CDN}/2026-10-16T21-06-23-047Z/aeroplane/help-video/course-client-resources-poster.webp`,
        page: "client" },
      { id: "client-state", title: "hv.clientState.title", summary: "hv.clientState.summary", duration: 93, src: `${CDN}/2026-10-16T21-07-53-698Z/aeroplane/help-video/course-client-state.mp4`,
        poster: `${CDN}/2026-10-16T21-07-54-760Z/aeroplane/help-video/course-client-state-poster.webp`,
        page: "client", hashes: ["report-client-state"] },
      { id: "client-ably", title: "hv.clientAbly.title", summary: "hv.clientAbly.summary", duration: 91, src: `${CDN}/2026-10-16T21-09-08-471Z/aeroplane/help-video/course-client-ably.mp4`,
        poster: `${CDN}/2026-10-16T21-09-09-418Z/aeroplane/help-video/course-client-ably-poster.webp`,
        page: "client" },
      { id: "client-debug", title: "hv.clientDebug.title", summary: "hv.clientDebug.summary", duration: 85, src: `${CDN}/2026-10-16T21-10-54-566Z/aeroplane/help-video/course-client-debug.mp4`,
        poster: `${CDN}/2026-10-16T21-10-55-605Z/aeroplane/help-video/course-client-debug-poster.webp`,
        page: "client", hashes: ["debug-trace"] },
      { id: "client-release", title: "hv.clientRelease.title", summary: "hv.clientRelease.summary", duration: 85, src: `${CDN}/2026-10-16T21-12-09-399Z/aeroplane/help-video/course-client-release.mp4`,
        poster: `${CDN}/2026-10-16T21-12-10-356Z/aeroplane/help-video/course-client-release-poster.webp`,
        page: "client", hashes: ["match-mode"] },
    ],
  },
  {
    id: "dev-flow", track: "server", title: "hv.course.devFlow.title", summary: "hv.course.devFlow.summary",
    lessons: [
      { id: "dev-pr", title: "hv.devPr.title", summary: "hv.devPr.summary", duration: 141, src: `${CDN}/2026-10-16T21-23-30-499Z/aeroplane/help-video/course-dev-pr.mp4`,
        poster: `${CDN}/2026-10-16T21-23-31-623Z/aeroplane/help-video/course-dev-pr-poster.webp`,
        page: "client" },
      { id: "dev-merge", title: "hv.devMerge.title", summary: "hv.devMerge.summary", duration: 134, src: `${CDN}/2026-10-16T21-25-36-253Z/aeroplane/help-video/course-dev-merge.mp4`,
        poster: `${CDN}/2026-10-16T21-25-37-265Z/aeroplane/help-video/course-dev-merge-poster.webp`,
        page: "client" },
      { id: "dev-release", title: "hv.devRelease.title", summary: "hv.devRelease.summary", duration: 141, src: `${CDN}/2026-10-21T02-23-17-223Z/aeroplane/help-video/course-dev-release.mp4`,
        poster: `${CDN}/2026-10-21T02-23-18-364Z/aeroplane/help-video/course-dev-release-poster.webp`,
        page: "client" },
    ],
  },
  {
    id: "operations", track: "operations", title: "hv.course.operations.title", summary: "hv.course.operations.summary",
    lessons: [
      { id: "ops-players", title: "hv.opsPlayers.title", summary: "hv.opsPlayers.summary", duration: 77, src: `${CDN}/2026-10-16T21-13-29-345Z/aeroplane/help-video/course-ops-players.mp4`,
        poster: `${CDN}/2026-10-16T21-13-30-379Z/aeroplane/help-video/course-ops-players-poster.webp`,
        page: "operations", hashes: ["report-players"] },
      { id: "ops-mail", title: "hv.opsMail.title", summary: "hv.opsMail.summary", duration: 90, src: `${CDN}/2026-10-17T00-47-23-077Z/aeroplane/help-video/course-ops-mail-zh-landscape.mp4`,
        poster: `${CDN}/2026-10-16T21-14-41-068Z/aeroplane/help-video/course-ops-mail-poster.webp`,
        page: "operations", hashes: ["mail"] },
      { id: "ops-release", title: "hv.opsRelease.title", summary: "hv.opsRelease.summary", duration: 88, src: `${CDN}/2026-10-16T21-15-50-606Z/aeroplane/help-video/course-ops-release.mp4`,
        poster: `${CDN}/2026-10-16T21-15-51-838Z/aeroplane/help-video/course-ops-release-poster.webp`,
        page: "operations" },
    ],
  },
  {
    id: "qa", track: "qa", title: "hv.course.qa.title", summary: "hv.course.qa.summary",
    lessons: [
      { id: "qa-setup", title: "hv.qaSetup.title", summary: "hv.qaSetup.summary", duration: 89, src: `${CDN}/2026-10-16T21-17-03-063Z/aeroplane/help-video/course-qa-setup.mp4`,
        poster: `${CDN}/2026-10-16T21-17-04-042Z/aeroplane/help-video/course-qa-setup-poster.webp`,
        page: "qa", hashes: ["dev-panel", "dev-token"] },
      { id: "qa-scenarios", title: "hv.qaScenarios.title", summary: "hv.qaScenarios.summary", duration: 85, src: `${CDN}/2026-10-16T21-18-07-986Z/aeroplane/help-video/course-qa-scenarios.mp4`,
        poster: `${CDN}/2026-10-16T21-18-08-987Z/aeroplane/help-video/course-qa-scenarios-poster.webp`,
        page: "qa" },
      { id: "qa-recovery", title: "hv.qaRecovery.title", summary: "hv.qaRecovery.summary", duration: 94, src: `${CDN}/2026-10-16T21-19-33-190Z/aeroplane/help-video/course-qa-recovery.mp4`,
        poster: `${CDN}/2026-10-16T21-19-34-282Z/aeroplane/help-video/course-qa-recovery-poster.webp`,
        page: "qa" },
      { id: "qa-report", title: "hv.qaReport.title", summary: "hv.qaReport.summary", duration: 93, src: `${CDN}/2026-10-16T21-21-08-960Z/aeroplane/help-video/course-qa-report.mp4`,
        poster: `${CDN}/2026-10-16T21-21-10-063Z/aeroplane/help-video/course-qa-report-poster.webp`,
        page: "qa", hashes: ["debug-trace"] },
    ],
  },
];

/**
 * 现成的课原样收进另一门课程：同一份视频，换一个课 id（courseOf / 上一课下一课按 id 找课程），
 * 不挂页面入口（hashes 去掉，否则配置页面包屑旁会出现两个一样的「看视频」）。学习进度按新 id 单独记。
 */
const reuse = (from: HelpVideoId, id: HelpVideoId): HelpVideoDef => {
  const { hashes: _hashes, ...v } = BASE_COURSES.flatMap((c) => c.lessons).find((x) => x.id === from)!;
  return { ...v, id };
};

/** 给 Hello ludo 团队的一套：飞行棋一期用到的后台，加服务端从哪下手。讲的是本仓库的后台与代码 */
const HELLO_LUDO: HelpCourseDef = {
  id: "hello-ludo", track: "hello-ludo", title: "hv.course.helloLudo.title", summary: "hv.course.helloLudo.summary",
  lessons: [
    { id: "hl-route", title: "hv.hlRoute.title", summary: "hv.hlRoute.summary", duration: 108, src: `${CDN}/2026-10-20T06-20-16-146Z/aeroplane/help-video/course-hl-route.mp4`,
      poster: `${CDN}/2026-10-20T06-20-17-517Z/aeroplane/help-video/course-hl-route-poster.webp`,
      page: "operations" },
    reuse("ver-publish", "hl-ver-publish"),
    reuse("init", "hl-init"),
    reuse("dice", "hl-dice"),
    reuse("spin", "hl-spin"),
    reuse("subgame", "hl-subgame"),
    reuse("talent", "hl-talent"),
    reuse("ptparams", "hl-ptparams"),
    reuse("ptstar", "hl-ptstar"),
    reuse("pttalent", "hl-pttalent"),
    { id: "hl-roll", title: "hv.hlRoll.title", summary: "hv.hlRoll.summary", duration: 107, src: `${CDN}/2026-10-20T05-22-18-227Z/aeroplane/help-video/course-hl-roll.mp4`,
      poster: `${CDN}/2026-10-20T05-22-19-334Z/aeroplane/help-video/course-hl-roll-poster.webp`,
      page: "client" },
    { id: "hl-enter", title: "hv.hlEnter.title", summary: "hv.hlEnter.summary", duration: 102, src: `${CDN}/2026-10-20T06-21-15-105Z/aeroplane/help-video/course-hl-enter.mp4`,
      poster: `${CDN}/2026-10-20T06-21-16-490Z/aeroplane/help-video/course-hl-enter-poster.webp`,
      page: "client" },
    reuse("client-resources", "hl-resources"),
    { id: "hl-map", title: "hv.hlMap.title", summary: "hv.hlMap.summary", duration: 121, src: `${CDN}/2026-10-20T05-29-48-337Z/aeroplane/help-video/course-hl-map.mp4`,
      poster: `${CDN}/2026-10-20T05-29-49-825Z/aeroplane/help-video/course-hl-map-poster.webp`,
      page: "client" },
    { id: "hl-new", title: "hv.hlNew.title", summary: "hv.hlNew.summary", duration: 127, src: `${CDN}/2026-10-20T05-41-16-565Z/aeroplane/help-video/course-hl-new.mp4`,
      poster: `${CDN}/2026-10-20T05-41-17-859Z/aeroplane/help-video/course-hl-new-poster.webp`,
      page: "client" },
    { id: "hl-gaps", title: "hv.hlGaps.title", summary: "hv.hlGaps.summary", duration: 136, src: `${CDN}/2026-10-20T06-23-19-856Z/aeroplane/help-video/course-hl-gaps.mp4`,
      poster: `${CDN}/2026-10-20T06-23-20-998Z/aeroplane/help-video/course-hl-gaps-poster.webp`,
      page: "client" },
    reuse("correct", "hl-correct"),
  ],
};

export const HELP_COURSES: HelpCourseDef[] = [...BASE_COURSES, HELLO_LUDO];

/**
 * 讲旧口径（B、E、收入膨胀 K、「× E × B」总览）的课。单掷金币版本里这几课不出现：
 * 页面上不该看出口径换过（leo 2026-10-22）
 */
export const LEGACY_ECON_LESSONS: ReadonlySet<HelpVideoId> = new Set<HelpVideoId>(["b", "e", "k", "overview"]);

/** 去掉旧口径课后的课程；一课不剩时返回 null（整门课不显示） */
export function withoutLegacyEcon(c: HelpCourseDef): HelpCourseDef | null {
  const lessons = c.lessons.filter((v) => !LEGACY_ECON_LESSONS.has(v.id));
  return !lessons.length ? null : lessons.length === c.lessons.length ? c : { ...c, lessons };
}

/** 全部课，按课程、课的顺序平铺 */
export const HELP_VIDEOS: HelpVideoDef[] = HELP_COURSES.flatMap((c) => c.lessons);

export const helpVideo = (id: HelpVideoId): HelpVideoDef => HELP_VIDEOS.find((v) => v.id === id)!;

/** 这一课属于哪门课程（上一课 / 下一课只在本门课程内走） */
export const courseOf = (id: HelpVideoId): HelpCourseDef => HELP_COURSES.find((c) => c.lessons.some((v) => v.id === id))!;

/** 挂在某个管端页面上的课（hashes 命中），按清单顺序；页面 id 不带 # */
export const lessonsForPage = (page: string): HelpVideoDef[] =>
  HELP_VIDEOS.filter((v) => (v.hashes ?? []).some((h) => (h.endsWith("*") ? page.startsWith(h.slice(0, -1)) : h === page)));

/** 视频传好了没有：没传的课入口照常显示，点开是「视频制作中」 */
export const lessonReady = (v: HelpVideoDef) => v.src !== "";

/** 一门课程已上线的课的总时长（秒） */
export const courseDuration = (c: HelpCourseDef) => c.lessons.reduce((s, v) => s + v.duration, 0);

/** 48 → "0:48" */
export const fmtDuration = (s: number) => {
  const seconds = Number.isFinite(s) ? Math.max(0, Math.floor(s)) : 0;
  return `${Math.floor(seconds / 60)}:${String(seconds % 60).padStart(2, "0")}`;
};

/** 课程中心的 hash：`#help-videos/cost` 打开课程中心并定位到「总造价」分区（useHashRoute 只认 / 前面那段） */
export const COURSE_PAGE = "help-videos";
export const courseHash = (id: HelpCourseId) => `${COURSE_PAGE}/${id}`;
